// F-006: Buffer polyfill for browser compatibility (must be first)
import { Buffer } from 'buffer'
globalThis.Buffer = Buffer

import { initSentry } from './lib/sentry'
import { initDatadogRum } from './lib/datadog-rum'
import { initAnalytics } from './lib/analytics'
// Sentry vor allem anderen initialisieren (AC-5)
initSentry()
// Datadog RUM nach Sentry initialisieren (X2.3)
initDatadogRum()
// Reichweitenmessung: laedt gtag mit Consent Mode v2 (Standard: denied), also
// bewusst VOR und unabhaengig vom Einwilligungsbanner. Ohne Zustimmung wird
// nichts auf dem Geraet gespeichert; siehe lib/analytics.ts.
initAnalytics()

import '@heliumtracker/design-tokens/tokens.css'
import './styles/base.css'
import './styles/utilities.css'
// F-R3-2: globale Druck-Regeln zuletzt, damit sie Shell-Styles ueberschreiben.
import './styles/print.css'
import '@heliumtracker/design-tokens/components.css'
// i18n (DE/EN) initialisieren — vor dem ersten Render
import './i18n'
import { StrictMode, lazy, Suspense, useMemo } from 'react'
import { createRoot } from 'react-dom/client'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ConnectionProvider, WalletProvider } from '@solana/wallet-adapter-react'
import { PhantomWalletAdapter } from '@solana/wallet-adapter-wallets'
import { clusterApiUrl } from '@solana/web3.js'
import { queryClient } from './lib/query-client'
import { routeTree } from './routeTree.gen'
import { ThemeProvider } from './providers/ThemeProvider'
import { attemptTokenRefresh, registerRouter } from './lib/api-client'
import { useAuthStore } from './stores/authStore'

// TanStack Router instance
const router = createRouter({ routeTree })

// Router beim api-client registrieren (für Logout-Redirect aus Interceptor)
registerRouter(({ to }) => router.navigate({ to }))

// Type registration for type-safe routing
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

// Devtools: lazy-loaded only in development
const ReactQueryDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-query-devtools').then((m) => ({
        default: m.ReactQueryDevtools,
      })),
    )
  : () => null

// FE-ROB-03: Obergrenze fuer den Bootstrap-Refresh.
const REFRESH_TIMEOUT_MS = 5000

// Dev-only Auth-Bootstrap fuer lokale Browser-/E2E-Validierung gegen das
// echte Backend: ?dev_refresh_token=<token> (z.B. per Rails-Console gemintet)
// wird VOR dem Silent-Refresh als Refresh-Token uebernommen und sofort aus
// der URL entfernt. Nur im Vite-Dev-Modus aktiv, im Produktionsbuild
// wegkompiliert (import.meta.env.DEV ist dort false-Literal).
function applyDevRefreshToken(): void {
  if (!import.meta.env.DEV) return
  const params = new URLSearchParams(window.location.search)
  const token = params.get('dev_refresh_token')
  if (!token) return
  sessionStorage.setItem('ht-refresh-token', token)
  params.delete('dev_refresh_token')
  const query = params.toString()
  window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''))
}

// Silent-Refresh beim App-Start (AC-2)
// Erst nach Abschluss des Refreshes wird die App gerendert.
async function initAuth(): Promise<void> {
  applyDevRefreshToken()
  const { getRefreshToken } = useAuthStore.getState()
  const refreshToken = getRefreshToken()

  if (!refreshToken) return // kein Token — direkt zu Login (Guard in _auth.tsx)

  // FE-ROB-03: Timeout nach 5s (R3-Mitigation).
  // Vorher wurde controller.signal NIE uebergeben — ein haengendes Backend
  // blockierte das erste Rendern bis zum Browser-Default (~300s, weisse Seite).
  // Jetzt wird das Signal bis in den fetch von doTokenRefresh() durchgereicht.
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS)

  // Ein gescheiterter Silent-Refresh darf eine noch GUELTIGE Session nicht
  // zerstoeren. Repro (lokal + Staging, 2026-09-02): `/api/v2/auth/login`
  // liefert einen Refresh-Token, den der Doorkeeper-Refresh-Grant mit dem
  // Web-Client ablehnt (`400 invalid_grant`, "was issued to another client").
  // Der Boot warf daraufhin per clearAuth() den frischen, noch stundenlang
  // gueltigen Access-Token aus sessionStorage — jeder Hard-Load/Deep-Link
  // (z.B. /people) landete auf /login. Aufgeraeumt wird nur noch, wenn wirklich
  // kein brauchbarer Access-Token mehr da ist; ein doch abgelaufener Token
  // faellt sauber in den 401-Interceptor des api-client.
  const dropSessionIfUnusable = () => {
    if (!useAuthStore.getState().isAccessTokenFresh()) {
      useAuthStore.getState().clearAuth()
    }
  }

  try {
    const newToken = await attemptTokenRefresh(controller.signal)
    if (!newToken) dropSessionIfUnusable()
  } catch {
    dropSessionIfUnusable()
  } finally {
    clearTimeout(timeout)
  }
}

// Wallet-Adapter-Konfiguration (W1.6)
// autoConnect={false} — kein stilles Connect ohne User-Aktion (AC-10)
// F-008: SolflareWalletAdapter entfernt — Solflare ist jetzt Standard-Wallet-konform
const SOLANA_ENDPOINT = clusterApiUrl('mainnet-beta')

function AppRoot() {
  const wallets = useMemo(
    () => [new PhantomWalletAdapter()],
    [],
  )

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ConnectionProvider endpoint={SOLANA_ENDPOINT}>
          <WalletProvider wallets={wallets} autoConnect={false}>
            <RouterProvider router={router} />
            <Suspense>
              <ReactQueryDevtools />
            </Suspense>
          </WalletProvider>
        </ConnectionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  )
}

// Bootstrap: Silent-Refresh, dann App rendern
initAuth().then(() => {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <AppRoot />
    </StrictMode>,
  )
})
